import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Modal, Field, ModalFooter } from "./Contacts";
import { Plus, Trash, FileText, PencilSimple } from "@phosphor-icons/react";
import { toast } from "sonner";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

export default function TemplatesPage() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // template object or "new"

  const load = () => api.listTemplates().then(setItems);
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-medium tracking-tight">Templates</h2>
          <p className="text-sm text-zinc-500 mt-0.5">Reusable email designs with merge tags.</p>
        </div>
        <button data-testid="btn-new-template" onClick={() => setEditing({ name: "", subject: "", html: "" })} className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-md bg-zinc-950 text-white hover:bg-zinc-800"><Plus size={14}/> New template</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map(t => (
          <div key={t.id} className="rounded-lg border border-zinc-200 bg-white p-4">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-8 h-8 rounded bg-zinc-100 flex items-center justify-center"><FileText size={14}/></div>
                <div className="min-w-0">
                  <div className="font-medium text-zinc-900 truncate flex items-center gap-1.5" data-testid={`tpl-${t.name}`}>
                    {t.name}
                    {(t.editor_mode || "richtext") === "blocks" && <span className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-full border border-indigo-200 bg-indigo-50 text-indigo-700 shrink-0">design</span>}
                  </div>
                  <div className="text-[11px] text-zinc-500 truncate">{t.subject}</div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setEditing(t)} className="text-zinc-400 hover:text-zinc-900"><PencilSimple size={14}/></button>
                <button onClick={async () => { if(window.confirm("Delete?")) { await api.deleteTemplate(t.id); load(); }}} className="text-zinc-400 hover:text-rose-700"><Trash size={14}/></button>
              </div>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full border border-dashed border-zinc-200 rounded-lg p-10 text-center text-sm text-zinc-500">No templates yet</div>}
      </div>

      {editing && <TemplateEditor t={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>}
    </div>
  );
}

function TemplateEditor({ t, onClose, onSaved }) {
  const [name, setName] = useState(t.name || "");
  const [subject, setSubject] = useState(t.subject || "");
  const [html, setHtml] = useState(t.html || "");
  const isBlocks = (t.editor_mode || "richtext") === "blocks";

  const save = async () => {
    const payload = isBlocks ? { name, subject } : { name, subject, html };
    if (t.id) await api.updateTemplate(t.id, payload);
    else await api.createTemplate({ name, subject, html });
    toast.success("Saved");
    onSaved();
  };

  return (
    <Modal title={t.id ? "Edit template" : "New template"} subtitle="Use {{first_name}} or {{company}} for merge tags." onClose={onClose} wide>
      <div className="space-y-3">
        <Field label="Name"><input data-testid="tpl-name" value={name} onChange={(e) => setName(e.target.value)} className="modal-input"/></Field>
        <Field label="Subject"><input data-testid="tpl-subject" value={subject} onChange={(e) => setSubject(e.target.value)} className="modal-input"/></Field>
        {isBlocks ? (
          <div className="rounded-md border border-indigo-200 bg-indigo-50/60 px-3 py-2.5 text-xs text-zinc-600" data-testid="tpl-blocks-note">
            This is a <strong>block design</strong> template ({(t.blocks || []).length} blocks). To change the design, load it into a campaign via "Load template…", edit the blocks, then use "Save as template" again. You can rename it and change the subject here.
          </div>
        ) : (
        <Field label="Body">
          <div data-testid="tpl-editor">
            <ReactQuill theme="snow" value={html} onChange={setHtml}/>
          </div>
        </Field>
        )}
      </div>
      <ModalFooter onClose={onClose}>
        <button data-testid="tpl-save" disabled={!name || !subject} onClick={save} className="modal-btn-primary">Save template</button>
      </ModalFooter>
    </Modal>
  );
}
